import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { projectStateAPI } from '../services/api';

// 🚀 PERFORMANCE: Local storage key for instant state caching
const LOCAL_STORAGE_KEY = 'okio_project_state_cache';

// 🚀 PERFORMANCE: Get cached state from localStorage for instant loading
const getCachedState = (projectId) => {
  try {
    const cached = localStorage.getItem(`${LOCAL_STORAGE_KEY}_${projectId}`);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.warn('Failed to load cached project state:', error);
    return null;
  }
};

// 🚀 PERFORMANCE: Cache state to localStorage for instant loading
const setCachedState = (projectId, state) => {
  try {
    localStorage.setItem(`${LOCAL_STORAGE_KEY}_${projectId}`, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to cache project state:', error);
  }
};

// Project Store - Centralized project state management with database persistence
export const useProjectStore = create(
  devtools(
    (set, get) => ({
      // === STATE ===
      currentProject: null,
      projectId: null,
      projects: [], // Cached project list
      isLoading: false,
      
      // === PROJECT STATE (persisted to database) ===
      selectedDocumentId: null,
      documentViewMode: 'code', // wysiwyg | code | view (changed default to code)
      sidebarSizes: { left: 300, right: 400 },
      activeTab: 'documents', // Sidebar tab: 'documents' or 'sources'
      activeChatId: null,
      
      // Chat section active tab (persistent)
      chatActiveTab: 'chat', // 'chat' or 'history'
      
      scrollPositions: {},
      customState: {},
      
      // Persistence state
      isStateSaving: false,
      isStateLoaded: false,
      saveTimeoutId: null,
      // 🚀 NEW: Track pending updates for batching
      pendingUpdates: {},
      lastSuccessfulSync: null,

      // === ACTIONS ===
      
      // Set current project
      setCurrentProject: (project) => {
        set({ 
          currentProject: project,
          projectId: project?.id || null
        });
        
        // Load project state when project changes
        if (project?.id) {
          get().loadProjectState(project.id);
        }
      },

      // Set project ID (for URL sync)
      setProjectId: (id) => {
        set({ projectId: id });
        
        // Load project state when project ID changes
        if (id) {
          get().loadProjectState(id);
        } else {
          // Clear state when no project ID
          set({ 
            selectedDocumentId: null,
            documentViewMode: 'code',
            sidebarSizes: { left: 300, right: 400 },
            activeTab: 'documents',
            activeChatId: null,
            chatActiveTab: 'chat',
            scrollPositions: {},
            customState: {},
            isStateLoaded: true,
            pendingUpdates: {},
            lastSuccessfulSync: null
          });
        }
      },

      // Set projects list
      setProjects: (projects) => {
        // console.log('🏗️ STORE: Setting projects list:', projects.length);
        set({ projects });
      },

      // Add project
      addProject: (project) => {
        const state = get();
        // console.log('➕ STORE: Adding project:', project.id);
        set({
          projects: [...state.projects, project]
        });
      },

      // Update project
      updateProject: (updatedProject) => {
        const state = get();
        // console.log('💾 STORE: Updating project:', updatedProject.id);
        set({
          // Update current project if it's the one being updated
          currentProject: state.currentProject?.id === updatedProject.id ? updatedProject : state.currentProject,
          // Update in projects cache
          projects: state.projects.map(proj => 
            proj.id === updatedProject.id ? updatedProject : proj
          )
        });
      },

      // Remove project
      removeProject: (projectId) => {
        const state = get();
        // console.log('🗑️ STORE: Removing project:', projectId);
        set({
          projects: state.projects.filter(proj => proj.id !== projectId),
          // Clear current project if removed project was current
          currentProject: state.currentProject?.id === projectId ? null : state.currentProject,
          projectId: state.projectId === projectId ? null : state.projectId
        });
      },

      // Set loading state
      setLoading: (isLoading) => {
        set({ isLoading });
      },

      // Set active chat ID
      setActiveChatId: (chatId) => {
        const state = get();
        set({ activeChatId: chatId });
        
        // Persist to database if we have a project
        if (state.projectId) {
          get().updateProjectState({ activeChatId: chatId });
        }
      },

      // Clear project state
      clearProject: () => {
        console.log('🚫 STORE: Clearing project state');
        set({ 
          currentProject: null, 
          projectId: null,
          // Clear persisted state
          selectedDocumentId: null,
          documentViewMode: 'code',
          sidebarSizes: { left: 300, right: 400 },
          activeTab: 'documents',
          activeChatId: null,
          chatActiveTab: 'chat',
          scrollPositions: {},
          customState: {},
          isStateLoaded: false,
          pendingUpdates: {},
          lastSuccessfulSync: null
        });
      },

      // === PROJECT STATE PERSISTENCE ===
      
      // 🚀 OPTIMIZED: Load project state with instant cache + background sync
      loadProjectState: async (projectId) => {
        try {
          // 🚀 INSTANT: Load from cache first for immediate UI response
          const cachedState = getCachedState(projectId);
          if (cachedState) {
            console.log('⚡ STORE: Loading cached project state for instant UI');
            set({
              selectedDocumentId: cachedState.selectedDocumentId || null,
              documentViewMode: cachedState.documentViewMode || 'code',
              sidebarSizes: cachedState.sidebarSizes || { left: 300, right: 400 },
              activeTab: cachedState.activeTab || 'documents',
              activeChatId: cachedState.activeChatId || null,
              chatActiveTab: cachedState.chatActiveTab || 'chat',
              scrollPositions: cachedState.scrollPositions || {},
              customState: cachedState.customState || {},
              isStateLoaded: true
            });
          } else {
            // Set defaults immediately if no cache
            set({ 
              selectedDocumentId: null,
              documentViewMode: 'code',
              sidebarSizes: { left: 300, right: 400 },
              activeTab: 'documents',
              activeChatId: null,
              chatActiveTab: 'chat',
              scrollPositions: {},
              customState: {},
              isStateLoaded: true
            });
          }
          
          // 🚀 BACKGROUND: Load from database to sync latest state
          const response = await projectStateAPI.getProjectState(projectId);
          
          if (response?.success && response?.data?.state) {
            const serverState = response.data.state;
            
            // Only update if server state is different from cached state
            if (!cachedState || JSON.stringify(serverState) !== JSON.stringify(cachedState)) {
              console.log('🔄 STORE: Syncing server state with local cache');
                          set({
              selectedDocumentId: serverState.selectedDocumentId || null,
              documentViewMode: serverState.documentViewMode || 'code',
              sidebarSizes: serverState.sidebarSizes || { left: 300, right: 400 },
                activeTab: serverState.activeTab || 'documents',
                activeChatId: serverState.activeChatId || null,
                chatActiveTab: serverState.chatActiveTab || 'chat',
                scrollPositions: serverState.scrollPositions || {},
                customState: serverState.customState || {},
                lastSuccessfulSync: Date.now()
              });
              
              // Update cache with server state
              setCachedState(projectId, serverState);
            } else {
              console.log('📦 STORE: Cache is up to date with server');
              set({ lastSuccessfulSync: Date.now() });
            }
          }
        } catch (error) {
          console.warn('⚠️ STORE: Failed to sync with server, using cached/default state:', error.message);
          // UI is already responsive from cache/defaults, so this is non-blocking
          set({ isStateLoaded: true });
        }
      },

      // 🚀 OPTIMIZED: Update project state with instant UI updates and smart batching
      updateProjectState: (updates) => {
        const state = get();
        
        if (!state.projectId) {
          console.warn('⚠️ STORE: No project ID, skipping state update');
          return;
        }

        // 🚀 INSTANT: Update UI state immediately (optimistic update)
        console.log('⚡ STORE: Instant UI update:', updates);
        set(updates);
        
        // 🚀 INSTANT: Update localStorage cache immediately
        const currentState = get();
        const stateToCache = {
          selectedDocumentId: currentState.selectedDocumentId,
          documentViewMode: currentState.documentViewMode,
          sidebarSizes: currentState.sidebarSizes,
          activeTab: currentState.activeTab,
          activeChatId: currentState.activeChatId,
          chatActiveTab: currentState.chatActiveTab,
          scrollPositions: currentState.scrollPositions,
          customState: currentState.customState
        };
        setCachedState(currentState.projectId, stateToCache);
        
        // 🚀 BATCHING: Collect updates for efficient database sync
        set({ 
          pendingUpdates: { ...state.pendingUpdates, ...updates }
        });
        
        // 🚀 REDUCED LATENCY: Trigger save with shorter debounce
        get().saveProjectState();
      },

      // 🚀 OPTIMIZED: Save project state with smart batching and error handling
      saveProjectState: async () => {
        const state = get();
        
        // Don't save if not loaded yet or no project
        if (!state.isStateLoaded || !state.projectId) {
          return;
        }

        // Clear existing timeout for debouncing
        if (state.saveTimeoutId) {
          clearTimeout(state.saveTimeoutId);
        }

        // 🚀 PERFORMANCE: Reduced debounce from 1000ms to 200ms
        const timeoutId = setTimeout(async () => {
          try {
            const currentState = get();
            
            // Skip if no pending updates
            if (!currentState.pendingUpdates || Object.keys(currentState.pendingUpdates).length === 0) {
              return;
            }
            
            console.log('💾 STORE: Syncing batched updates to server:', currentState.pendingUpdates);
            
            // 🚀 FIX: Use scheduler to avoid flushSync warning
            // Defer the state update to next tick to avoid render conflicts
            setTimeout(() => {
              set({ isStateSaving: true });
            }, 0);

            // Send only the pending updates (not the entire state)
            await projectStateAPI.updateProjectState(currentState.projectId, currentState.pendingUpdates);
            
            console.log('✅ STORE: Project state synced successfully');
            
            // Clear pending updates on successful save
            set({ 
              pendingUpdates: {},
              lastSuccessfulSync: Date.now(),
              isStateSaving: false
            });
            
          } catch (error) {
            console.warn('⚠️ STORE: Failed to sync project state to server:', error.message);
            
            // 🚀 RESILIENCE: Don't show error to user - state is still saved locally
            // The next successful save will include these updates
            console.log('📦 STORE: Updates preserved locally, will retry on next change');
            
            // Show a subtle warning only if we haven't synced for a while
            const timeSinceLastSync = Date.now() - (get().lastSuccessfulSync || 0);
            if (timeSinceLastSync > 300000) { // 5 minutes
              console.warn('⏰ STORE: State hasn\'t synced to server for 5+ minutes');
              // Could optionally show a subtle notification here
            }
            
            // Ensure we clear the saving state even on error
            set({ isStateSaving: false });
          } finally {
            set({ saveTimeoutId: null });
          }
        }, 200); // 🚀 REDUCED: From 1000ms to 200ms for much better responsiveness

        set({ saveTimeoutId: timeoutId });
      },


      // === SELECTORS ===
      
      // Get project by ID from cache
      getProjectById: (id) => {
        const state = get();
        return state.projects.find(proj => proj.id === id);
      },

      // Check if a project is selected
      hasCurrentProject: () => {
        const state = get();
        return !!state.currentProject;
      }
    }),
    {
      name: 'project-store', // For Redux DevTools
    }
  )
); 